import { ref } from 'vue'

import batchService from '@/api/batch'
import { generateUuidV4 } from '@/utils/uuid'

export function useMoveSamplesBatch() {
  const state = ref('IDLE')
  const summary = ref(null)
  const results = ref([])
  const retryAfter = ref(null)
  const lastIdempotencyKey = ref(null)
  const lastError = ref(null)
  let lastItems = []

  function reset() {
    state.value = 'IDLE'
    summary.value = null
    results.value = []
    retryAfter.value = null
    lastIdempotencyKey.value = null
    lastError.value = null
    lastItems = []
  }

  async function moveSamplesBatch(items, options = {}) {
    state.value = 'LOADING'
    lastItems = Array.isArray(items) ? items : []
    const idempotencyKey = generateUuidV4()
    lastIdempotencyKey.value = idempotencyKey

    const payload = {
      idempotencyKey,
      items: lastItems,
    }

    if (options && options.targetBatchCreation) {
      payload.targetBatchCreation = options.targetBatchCreation
    }

    try {
      const response = await batchService.moveSamplesBatch(payload)

      const data = response && response.data ? response.data : {}
      summary.value = data.summary || null
      results.value = Array.isArray(data.results) ? data.results : []
      lastError.value = null

      const failed = summary.value && summary.value.failed ? summary.value.failed : 0
      const outboxQueued =
        summary.value && summary.value.outboxQueued ? summary.value.outboxQueued : 0

      state.value = failed === 0 && outboxQueued === 0 ? 'SUCCESS_TOTAL' : 'PARTIAL'
      return state.value
    } catch (error) {
      const status = error && error.response ? error.response.status : null
      const headers = error && error.response ? error.response.headers : null
      retryAfter.value = headers && headers['retry-after'] ? headers['retry-after'] : null
      lastError.value = error && error.response ? error.response : null

      if (status === 409) {
        state.value = 'CONFLICT'
      } else if (status === 503) {
        state.value = 'OVERLOAD'
      } else {
        state.value = 'ERROR'
      }

      return state.value
    }
  }

  async function retryFailed() {
    const failedBarcodes = (results.value || [])
      .filter(r => r.status !== 'OK' && r.status !== 'OUTBOX_QUEUED')
      .map(r => r.barcode)

    const filtered = lastItems.filter(i => failedBarcodes.includes(i.barcode))

    if (filtered.length === 0) {
      return state.value
    }

    const targetBatchIdentifier =
      summary.value && summary.value.targetBatchIdentifier
        ? summary.value.targetBatchIdentifier
        : null

    const itemsToRetry = targetBatchIdentifier
      ? filtered.map(item => ({ ...item, targetBatchIdentifier }))
      : filtered

    return moveSamplesBatch(itemsToRetry)
  }

  return {
    state,
    summary,
    results,
    retryAfter,
    lastIdempotencyKey,
    lastError,
    moveSamplesBatch,
    retryFailed,
    reset,
  }
}
